import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { UsersIcon, CalendarIcon, HomeIcon, LandmarkIcon, DollarSignIcon, HeartHandshakeIcon, ChevronDownIcon, FileTextIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface CounterCardProps {
  value: number;
  suffix: string;
  label: string;
  sublabel: string;
  borderColor: string;
  accentColor: string;
  Icon: ComponentType<{
    className?: string;
  }>;
  delay: number;
  stripeColors: string[];
}
function useCounter(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}
function formatCount(n: number): string {
  if (n >= 1000000000) {
    const val = n / 1000000000;
    return (Number.isInteger(val) ? val.toString() : val.toFixed(1)) + 'B';
  }
  if (n >= 1000000) {
    const val = n / 1000000;
    return (Number.isInteger(val) ? val.toString() : val.toFixed(1)) + 'M';
  }
  if (n >= 1000) {
    return (n / 1000).toFixed(0) + 'K';
  }
  return n.toLocaleString();
}
function CounterCard({
  value,
  suffix,
  label,
  sublabel,
  borderColor,
  accentColor,
  Icon,
  delay,
  stripeColors
}: CounterCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const count = useCounter(value, 2000, inView);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50,
    scale: 0.9
  }} animate={inView ? {
    opacity: 1,
    y: 0,
    scale: 1
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative bg-white rounded-2xl shadow-xl overflow-hidden group card-hover" data-id="element-343">
      {/* Flag stripe top border */}
      <div className="h-2 w-full flex" data-id="element-344">
        {stripeColors.map((color, i) => <div key={i} className="flex-1" style={{
        backgroundColor: color
      }} data-id="element-345" />)}
      </div>

      {/* Thick colored left accent */}
      <div className={`absolute left-0 top-2 bottom-0 w-1.5 ${borderColor}`} data-id="element-346" />

      <div className="p-8 pl-10" data-id="element-347">
        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${accentColor} bg-opacity-10 mb-4`} data-id="element-348">
          <Icon className={`w-6 h-6 ${accentColor}`} data-id="element-349" />
        </div>

        {/* Counter */}
        <div className="flex items-end gap-1 mb-2" data-id="element-350">
          <span className={`font-bebas text-6xl sm:text-7xl leading-none tracking-wider ${accentColor}`} data-id="element-351">
            {formatCount(count)}
          </span>
          <span className={`font-bebas text-4xl leading-none ${accentColor} mb-1`} data-id="element-352">
            {suffix}
          </span>
        </div>

        {/* Label */}
        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider leading-none mb-1" data-id="element-353">
          {label}
        </h3>
        <p className="font-nunito text-gray-500 text-sm" data-id="element-354">{sublabel}</p>
      </div>

      {/* Decorative corner */}
      <div className={`absolute bottom-0 right-0 w-20 h-20 rounded-tl-full opacity-10 ${borderColor}`} data-id="element-355" />
    </motion.div>;
}
export function CommunityStrength() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [factsOpen, setFactsOpen] = useState(false);
  const [hondurasFactsOpen, setHondurasFactsOpen] = useState(false);
  const {
    t
  } = useLanguage();
  return <section id="community" className="bg-gray-50 py-16 lg:py-24" aria-labelledby="strength-heading" data-id="element-356">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-357">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-16" data-id="element-358">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-359">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-360">
              {t('community.label')}
            </span>
            <h2 id="strength-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-361">
              {t('community.heading')}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-362">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-363" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-364" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-365" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-366">
              {t('community.description')}
            </p>
          </motion.div>
        </div>

        {/* Counter Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-367">
          <CounterCard value={1800000} suffix="+" label={t('community.card1.label')} sublabel={t('community.card1.sublabel')} borderColor="bg-haa-skyblue text-haa-skyblue" accentColor="text-haa-skyblue" Icon={UsersIcon} delay={0} stripeColors={['#4A90D9', '#ffffff', '#4A90D9']} data-id="element-368" />
          <CounterCard value={9000000000} suffix="+" label={t('community.card2.label')} sublabel={t('community.card2.sublabel')} borderColor="bg-haa-midblue text-haa-midblue" accentColor="text-haa-midblue" Icon={DollarSignIcon} delay={0.1} stripeColors={['#1565C0', '#ffffff', '#1565C0']} data-id="element-369" />
          <CounterCard value={31} suffix="%" label={t('community.card3.label')} sublabel={t('community.card3.sublabel')} borderColor="bg-haa-blue text-haa-blue" accentColor="text-haa-blue" Icon={HomeIcon} delay={0.2} stripeColors={['#003F87', '#ffffff', '#003F87']} data-id="element-370" />
          <CounterCard value={250} suffix="+" label={t('community.card4.label')} sublabel={t('community.card4.sublabel')} borderColor="bg-haa-navy text-haa-navy" accentColor="text-haa-navy" Icon={HeartHandshakeIcon} delay={0.3} stripeColors={['#001A3E', '#ffffff', '#001A3E']} data-id="element-371" />
        </div>

        {/* Honduras Facts Dropdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.45,
        duration: 0.5
      }} className="mt-10 max-w-4xl mx-auto" data-id="element-372">
          <div className="rounded-2xl border-l-4 border-haa-blue bg-white shadow-lg overflow-hidden" data-id="element-373">
            <button onClick={() => setHondurasFactsOpen(!hondurasFactsOpen)} aria-expanded={hondurasFactsOpen} className="w-full flex items-center justify-center px-6 py-5 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-374">
              <div className="flex items-center gap-3" data-id="element-375">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-376">
                  <FileTextIcon className="w-5 h-5 text-white" data-id="element-377" />
                </div>
                <span className="font-bebas text-2xl tracking-wider text-haa-navy" data-id="element-378">
                  {t('community.hondurasFacts')}
                </span>
                <motion.div animate={{
                rotate: hondurasFactsOpen ? 180 : 0
              }} transition={{
                duration: 0.25,
                ease: [0.16, 1, 0.3, 1]
              }} className="flex-shrink-0" data-id="element-379">
                  <ChevronDownIcon className="w-6 h-6 text-haa-blue" data-id="element-380" />
                </motion.div>
              </div>
            </button>
            <AnimatePresence initial={false} data-id="element-381">
              {hondurasFactsOpen && <motion.div key="honduras-facts-content" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.35,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden" data-id="element-382">
                  <div className="border-t border-gray-100 px-6 py-5" data-id="element-383">
                    <h4 className="font-bebas text-lg tracking-wider text-haa-blue mb-3" data-id="element-384">
                      {t('community.hondurasFactsSubheading')}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-385">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(num => <div key={num} className="bg-blue-50 rounded-xl px-4 py-3 border border-blue-100" data-id="element-386">
                            <p className="font-nunito text-xs text-gray-500 uppercase tracking-wider font-semibold" data-id="element-387">
                              {t(`community.fact${num}.label`)}
                            </p>
                            <p className="font-nunito text-sm text-haa-navy font-bold mt-0.5" data-id="element-388">
                              {t(`community.fact${num}.value`)}
                            </p>
                          </div>)}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Honduras Reports & Resources Dropdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.5
      }} className="mt-10 max-w-4xl mx-auto" data-id="element-389">
          <div className="rounded-2xl border-l-4 border-haa-blue bg-white shadow-lg overflow-hidden" data-id="element-390">
            <button onClick={() => setFactsOpen(!factsOpen)} aria-expanded={factsOpen} className="w-full flex items-center justify-center px-6 py-5 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-391">
              <div className="flex items-center gap-3" data-id="element-392">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-393">
                  <FileTextIcon className="w-5 h-5 text-white" data-id="element-394" />
                </div>
                <span className="font-bebas text-2xl tracking-wider text-haa-navy" data-id="element-395">
                  {t('community.reportsResources')}
                </span>
                <motion.div animate={{
                rotate: factsOpen ? 180 : 0
              }} transition={{
                duration: 0.25,
                ease: [0.16, 1, 0.3, 1]
              }} className="flex-shrink-0" data-id="element-396">
                  <ChevronDownIcon className="w-6 h-6 text-haa-blue" data-id="element-397" />
                </motion.div>
              </div>
            </button>

            <AnimatePresence initial={false} data-id="element-398">
              {factsOpen && <motion.div key="facts-content" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.35,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden" data-id="element-399">
                  <div className="border-t border-gray-100 px-6 py-5" data-id="element-400">
                    {/* Reports & Resources */}
                    <div data-id="element-401">
                      <h4 className="font-bebas text-lg tracking-wider text-haa-blue mb-3" data-id="element-402">
                        {t('community.reportsResources')}
                      </h4>
                      <div className="space-y-2" data-id="element-403">
                        {[{
                      num: 1,
                      url: 'https://data.census.gov/table/ACSSPP1Y2024.S0201?q=honduran'
                    }, {
                      num: 20,
                      url: 'https://budgetmodel.wharton.upenn.edu/p/2025-11-19-550-000-workers-lose-status-by-end-of-2025/'
                    }, {
                      num: 21,
                      url: 'https://www.fwd.us/news/temporary-protected-status-report-2025/'
                    }, {
                      num: 6,
                      url: 'https://www.pewresearch.org/hispanic/fact-sheet/us-hispanics-facts-on-honduran-origin-latinos/'
                    }, {
                      num: 4,
                      url: 'https://www.migrationpolicy.org/country-resource/honduras'
                    }, {
                      num: 5,
                      url: 'https://www.migrationpolicy.org/data/unauthorized-immigrant-population/state/US'
                    }, {
                      num: 17,
                      url: 'https://sfo2.digitaloceanspaces.com/itep/ITEP-Tax-Payments-by-Undocumented-Immigrants-2024.pdf'
                    }, {
                      num: 18,
                      url: 'https://www.brookings.edu/articles/climate-violence-and-honduran-migration-to-the-united-states/'
                    }, {
                      num: 19,
                      url: 'https://www.everycrsreport.com/files/2023-11-30_R47856_509b38f111c97e00b36f10716d1716a033b17287.pdf'
                    }, {
                      num: 2,
                      url: 'https://www.worldbank.org/en/country/honduras/overview'
                    }, {
                      num: 3,
                      url: 'https://hdr.undp.org/data-center/specific-country-data#/countries/HND'
                    }, {
                      num: 7,
                      url: 'https://www.iadb.org/en/who-we-are/country-offices/honduras'
                    }, {
                      num: 8,
                      url: 'https://crisisresponse.iom.int/response/honduras-crisis-response-plan-2023-2025'
                    }, {
                      num: 9,
                      url: 'https://www.asj-us.org/stories'
                    }, {
                      num: 10,
                      url: 'https://www.hrw.org/world-report/2026/country-chapters/honduras'
                    }, {
                      num: 11,
                      url: 'https://statistics.cepal.org/portal/cepalstat/index.html?lang=en'
                    }, {
                      num: 12,
                      url: 'https://freedomhouse.org/country/honduras/freedom-world/2025'
                    }, {
                      num: 13,
                      url: 'https://www.unocha.org/publications/report/honduras/honduras-humanitarian-needs-and-response-plan-2025-summary'
                    }, {
                      num: 14,
                      url: 'https://reliefweb.int/report/honduras/wfp-honduras-country-brief-october-2025'
                    }, {
                      num: 15,
                      url: 'https://reliefweb.int/report/honduras/honduras-necesidades-humanitarias-remanentes-enero-2026'
                    }, {
                      num: 16,
                      url: 'https://open.unicef.org/country/Honduras?cid=1860'
                    }, {
                      num: 22,
                      url: 'https://inm.gob.hn/retornados.html'
                    }].map(report => <a key={report.num} href={report.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 hover:bg-blue-50 hover:border-blue-200 transition-colors group" data-id="element-404">
                            <div className="flex-1 min-w-0" data-id="element-405">
                              <p className="font-nunito font-bold text-sm text-haa-navy group-hover:text-haa-blue transition-colors leading-snug" data-id="element-406">
                                {t(`community.report${report.num}.title`)}
                              </p>
                              <p className="font-nunito text-xs text-gray-500 leading-relaxed mt-0.5" data-id="element-407">
                                {t(`community.report${report.num}.description`)}
                              </p>
                            </div>
                            <ExternalLinkIcon className="w-4 h-4 text-gray-300 group-hover:text-haa-blue flex-shrink-0 mt-0.5 transition-colors" data-id="element-408" />
                          </a>)}
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Bottom decorative element — Honduran flag: blue | white | blue */}
        <motion.div initial={{
        opacity: 0,
        scaleX: 0
      }} animate={titleInView ? {
        opacity: 1,
        scaleX: 1
      } : {}} transition={{
        duration: 1,
        delay: 0.5
      }} className="mt-16 h-1 w-full rounded-full overflow-hidden flex" data-id="element-409">
          <div className="flex-1 bg-haa-blue" data-id="element-410" />
          <div className="flex-1 bg-white border-y border-gray-200" data-id="element-411" />
          <div className="flex-1 bg-haa-blue" data-id="element-412" />
        </motion.div>
      </div>
    </section>;
}